import {
  CascaderField,
  DateRangePickerField,
  Form,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useAccountingTypeOptions, useRevenueNatureOptions } from '../../hooks';
import {
  RevenueNatureFormSchema,
  RevenueNatureLevelLabelModifier,
  revenueNatureLevelType,
} from '../../models';
import { revenueNatureCodeLength } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof RevenueNatureFormSchema>;
type InitialFormValues = Partial<FormValues>;

type Props = {
  type: OperationType;
  initialValues?: InitialFormValues;
  onSubmit: (values: FormValues) => void;
};

export const RevenueNatureForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    setValue,
    clearErrors,
    reset,
  } = useForm<InitialFormValues, FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(RevenueNatureFormSchema),
  });

  const level = watch('level');
  const accountingOptions = useAccountingTypeOptions({ showCode: true });
  const options = useRevenueNatureOptions(level ? level - 1 : 0);
  const levelOptions = [
    // TODO:
    // 愛媛県は細々節を利用していないため、細々節の選択肢を削除
    // SaaS 化に向けては対応方針の検討が必要
    // Ehime prefecture does not use level 6, so it is removed at the moment.
    // We need to consider the policy for SaaS migration.
    ...Array(revenueNatureLevelType.options.length - 1),
  ].map((_, idx) => ({
    label: RevenueNatureLevelLabelModifier[idx + 1],
    value: idx + 1,
  }));

  const codeMaxValue = getCodeMaxValue(revenueNatureCodeLength(level === 4));

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="level"
        label={t('level')}
        control={control}
        options={levelOptions}
        errors={errors}
        disabled={type === 'edit'}
        shouldCoexistOnChange
        onChange={(value) => {
          if (value !== level) {
            clearErrors();
            reset(initialValues);
            setValue('level', value);
          }
        }}
        required
      />

      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        options={accountingOptions.data}
        errors={errors}
        required={level === 1}
        disabled={type === 'edit' || level !== 1}
      />

      <CascaderField
        name="upperIds"
        label={t('upper-revenue-nature')}
        control={control}
        options={options.data}
        errors={errors}
        disabled={!level || level === 1}
        required={(level ?? 0) > 1}
      />

      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={codeMaxValue}
        shouldCoexistOnChange
        onChange={(value) => {
          if (value && value <= codeMaxValue && errors.code?.type === 'max') {
            clearErrors('code');
          }
        }}
        required
        disabled={!level}
      />

      <TextInputField
        name="name"
        autocompleteKey="revenueNature.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
